import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useCreateSponsorship } from "@/api/sponsorships";
import { useAuth } from "@/contexts/AuthContext";
import PropTypes from "prop-types";

const formSchema = z.object({
  sponsorName: z.string().min(2, { message: "Sponsor name is required" }),
  sponsorPhone: z.string().min(1, { message: "Phone is required" }),
  place: z.string().min(1, { message: "Place is required" }),
  amountPromised: z.string().min(1, { message: "Amount is required" }),
  currency: z.string().default("INR"),
  careOfName: z.string().min(2, { message: "Care of name is required" }),
  careOfPhone: z.string().regex(/^[+]?\d{1,15}$/, {
    message: "Please enter a valid phone number (maximum 15 digits)",
  }),
});

export function AddIndividualSponsorshipForm({ onSuccess }) {
  const { user } = useAuth();
  const { mutate: createSponsorship, isPending } = useCreateSponsorship();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sponsorName: "",
      sponsorPhone: "",
      place: "",
      amountPromised: "",
      currency: "INR",
      careOfName: "",
      careOfPhone: "",
    },
  });

  function onSubmit(values) {
    createSponsorship(
      {
        type: "Individual",
        sponsorName: values.sponsorName,
        sponsorPhone: values.sponsorPhone || undefined,
        chapter: user?.chapter,
        place: values.place,
        amountPromised: parseInt(values.amountPromised, 10),
        currency: values.currency,
        careOf: {
          name: values.careOfName || undefined,
          phone: values.careOfPhone || undefined,
        },
      },
      {
        onSuccess: () => {
          form.reset();
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Sponsor Details */}
        <FormField
          control={form.control}
          name="sponsorName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Sponsor Name <span className="text-rose-600 ml-1">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter sponsor name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="sponsorPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Phone <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Enter phone" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="place"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Place <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Enter place" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Amount */}
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="amountPromised"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Amount Promised <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="number" placeholder="Amount" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="INR">₹ INR</SelectItem>
                    <SelectItem value="SAR">﷼ SAR</SelectItem>
                    <SelectItem value="QAR">ر.ق QAR</SelectItem>
                    <SelectItem value="AED">د.إ AED</SelectItem>
                    <SelectItem value="KWD">د.ك KWD</SelectItem>
                    <SelectItem value="OMR">ر.ع. OMR</SelectItem>
                    <SelectItem value="BHD">.د.ب BHD</SelectItem>
                    <SelectItem value="MYR">RM MYR</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Care Of */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="careOfName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  C/O Name <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Care of name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="careOfPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  C/O Phone <span className="text-rose-600 ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Care of phone" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Add Individual Sponsorship"
          )}
        </Button>
      </form>
    </Form>
  );
}

AddIndividualSponsorshipForm.propTypes = {
  onSuccess: PropTypes.func,
};
